import React from 'react';

import MetricLoadingView from '../../../components/MetricLoadingView.jsx';

import {
    RIQInfoPopover
} from '../../../components/RIQPopover.jsx';

import RIQModal from '../../../components/RIQModal.jsx';

import {
    useCurationConfigValue
} from '../../../hooks/widgetConfiguration.js';

import useGetBrowserLanguage from '../hooks/useGetBrowserLanguage.js';

import useMetrics from '../../../hooks/useMetrics.js';

import {
    listeningDefinitions
} from './enums/listeningDefinitions.jsx';

import InstantSearchTips from './InstantSearchTips.jsx';

const {
    description,
    NUM_RECOMMENDATIONS_TO_GENERATE,
    title
} = listeningDefinitions['search-recommendations'];

export function useGenerateCompanyQuery(companyId) {
    const landscapeId = useCurationConfigValue('landscapeId');
    const defaultLanguage = useGetBrowserLanguage();

    const metricRequests = {};
    if (companyId) {
        metricRequests.query = {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH_RECOMMENDATIONS,
            dimensions: ['recommendationId'],
            measure: 'query',
            filters: {
                company: {
                    landscapeId: landscapeId,
                    onlyCompanyIds: [companyId]
                },
                listening: {
                    language: defaultLanguage
                }
            }
        };
    }

    const {error, isLoading, data: metricResults} = useMetrics(metricRequests);

    let query;
    if (metricResults) {
        const collatedResults = RivalIQ.metrics.new.MetricResponse.groupResponsesByFixedDimensions(metricResults);

        query = collatedResults?.[0]?.query?.[1];
        if (query) {
            query.language ??= defaultLanguage;
        }
    }

    return {
        error,
        isLoading: isLoading && companyId,
        query
    };
}

function makeCompanyNamesRequest(landscapeId) {
    const companyNamesRequest = {
        name: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.COMPANIES,
            dimensions: ['companyId'],
            measure: 'name',
            filters: {
                company: {
                    landscapeId
                }
            }
        }
    };

    return companyNamesRequest;
}

export function GeneratingQueryModal({companyId}) {
    const landscapeId = useCurationConfigValue('landscapeId');

    const companyNamesRequest = makeCompanyNamesRequest(landscapeId);

    const {data: companyNameResults} = useMetrics(companyNamesRequest, {
        requestGroup: 'companyName'
    });

    const companyName = _.find(companyNameResults?.name?.data, result => result[0] === companyId)?.[1];

    let companyNameText = null;
    if (companyName) {
        companyNameText = (
            <div>{companyName}</div>
        );
    }

    const message = `Using AI to generate keywords${companyName ? ' about' : ''}`;

    return (
        <RIQModal
            showModal={true}
            variant='small'
        >
            <div className='tlw-m-15 tlw-flex tlw-min-h-[180px] tlw-flex-col tlw-place-items-center tlw-gap-10'>
                <div className='tlw-text-[36px] tlw-text-gray-500'>
                    <span className='fa fa-circle-o-notch fa-spin'></span>
                </div>

                <div className='tlw-grow tlw-text-center tlw-text-md tlw-leading-24'>
                    <div className='tlw-font-bold'>{message}</div>
                    {companyNameText}
                </div>
            </div>
        </RIQModal>
    );
}

function CompanyList({instanceId, onCompanyClicked}) {

    const landscapeId = useCurationConfigValue(instanceId, 'landscapeId');

    const focusCompanyId = useCurationConfigValue(instanceId, 'focusCompanyId');

    const companyNamesRequest = makeCompanyNamesRequest(landscapeId);

    return (
        <MetricLoadingView
            instanceId={instanceId}
            metricRequests={companyNamesRequest}
        >
            {
                (results) => {
                    const collateResults = RivalIQ.metrics.new.MetricResponse.groupResponsesByFixedDimensions(results);

                    return (
                        <div
                            className='tlw-w-full'
                        >
                            {_.map(pickCompaniesToDisplay(collateResults, focusCompanyId), function ({name: [
                                companyId,
                                companyName
                            ]}) {
                                return (
                                    <div
                                        key={companyId}
                                        className='tlw-w-full tlw-border tlw-border-l-0 tlw-border-r-0 tlw-border-t-0 tlw-border-solid tlw-border-gray-350'
                                    >

                                        <button
                                            className='tlw-flex tlw-h-full tlw-w-full tlw-items-center tlw-border-0 tlw-bg-transparent tlw-py-10 tlw-text-sm tlw-text-gray-700 hover_tlw-text-blue-400'
                                            onClick={() => onCompanyClicked(companyId, companyName)}
                                        >
                                            <span
                                                className='tlw-grow tlw-text-left hover_tlw-underline'
                                            >
                                                {companyName}
                                            </span>
                                            <span
                                                className='fa fa-fw fa-chevron-right'
                                            />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    );
                }
            }
        </MetricLoadingView>
    );
}

export function SearchRecommendations({instanceId}) {

    function onCompanyClicked(companyId, companyName) {
        RivalIQ.execute('history:navigate', `listening/search?companyId=${companyId}`, {
            trigger: true
        });
    }

    return (
        <div
            className='tlw-flex tlw-min-h-[210px] tlw-w-full tlw-max-w-[400px] tlw-flex-col tlw-items-center tlw-gap-10'
        >

            <div
                className='tlw-whitespace-nowrap tlw-text-sm tlw-font-bold tlw-uppercase tlw-text-gray-700'
            >
                {title}
                <RIQInfoPopover
                    description={description}
                />
            </div>


            <CompanyList
                instanceId={instanceId}
                onCompanyClicked={onCompanyClicked}
            />
            <InstantSearchTips/>
        </div>
    );
}

function pickCompaniesToDisplay(collatedResults, focusCompanyId) {
    // return an array with length NUM_RECOMMENDATIONS_TO_GENERATE results
    // if focusCompanyId is provided then it will be the first result in the array

    // sort the results so the focusCompanyId is first
    const sortedResults = _.sortBy(collatedResults, function ({name: [
        companyId
    ]}) {
        return companyId === focusCompanyId ? 0 : 1;
    });

    return _(sortedResults)
        .filter(company => company.name[1])
        .take(NUM_RECOMMENDATIONS_TO_GENERATE)
        .value();
}
